/**
 * The team list, the invite flow and the per-person edit drawer.
 *
 * Two house rules shape this screen. First, the per-row controls are fixed-size
 * icon buttons in a fixed-width track, so they land on the same x on every row
 * without anybody measuring anything. Second, an invite is a LINK, never a
 * generated password: the modal shows the URL to copy and says plainly that the
 * person sets their own password.
 *
 * The API refuses a near-duplicate with 409 `possible_duplicate`, and that is
 * surfaced as a question rather than an error. Creating a second identity for
 * somebody who already exists splits their history in half.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Mail, Pencil, Phone, Search, UserPlus } from 'lucide-react';
import { api, ApiError, qk, type Role, type StaffMember } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { fmtMoney } from '../../lib/time.ts';
import { Avatar, EmptyState, ErrorState, Field, LoadingBlock, PhoneLink, Spinner } from '../../components/Bits.tsx';
import { Drawer, Modal, OutcomeModal } from '../../components/Overlay.tsx';

const ROLE_LABEL: Record<Role, string> = {
  owner: 'Owner', manager: 'Manager', staff: 'Team member', dev: 'Sneaky Panda support',
};

/** Who runs the place sorts to the top: owners, then managers, then everyone else. */
const ROLE_RANK: Record<Role, number> = { owner: 0, manager: 1, staff: 2, dev: 3 };

/**
 * Row identity per role. Team members get nothing on purpose: they are most of
 * the list, and tinting every row tints none of them.
 */
const ROLE_TINT: Partial<Record<Role, { fill: string; rail: string }>> = {
  owner: { fill: 'var(--sp-role-owner-soft)', rail: 'var(--sp-role-owner)' },
  manager: { fill: 'var(--sp-role-manager-soft)', rail: 'var(--sp-role-manager)' },
  dev: { fill: 'var(--sp-role-dev-soft)', rail: 'var(--sp-role-dev)' },
};

/** A form field is text even when the input is type=number. */
const money = (v: unknown): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').trim());
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

/** Rank, then name, so the order is stable and readable rather than insertion order. */
function byRoleThenName(a: StaffMember, b: StaffMember): number {
  const rank = ROLE_RANK[a.role] - ROLE_RANK[b.role];
  return rank !== 0 ? rank : a.name.localeCompare(b.name);
}

/**
 * One person on the list. Shared by the team list and the platform access
 * list so both keep the same aligned control track. A support account has no
 * rates to show, so that column reads as not applicable rather than $0.00.
 */
function MemberRow({ s, first, onEdit }: { s: StaffMember; first: boolean; onEdit: () => void }) {
  const isSupport = s.role === 'dev';
  const tint = ROLE_TINT[s.role];
  return (
    <div
      className="member-row flex items-center gap-3 py-3 pr-4"
      style={{
        borderTop: first ? undefined : '1px solid var(--sp-hairline)',
        opacity: s.active === false ? 0.55 : 1,
        background: tint?.fill,
        // The rail is drawn as a left border so the tint and the marker cannot
        // drift apart, and the padding compensates so every row's content
        // still starts on the same x whether it is tinted or not.
        borderLeft: `3px solid ${tint?.rail ?? 'transparent'}`,
        paddingLeft: 13,
      }}
    >
      <Avatar name={s.name} size={38} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-[14.5px] font-bold text-ink">{s.name}</span>
          {s.role !== 'staff' ? (
            <span className={`chip chip-role-${s.role}`}>{ROLE_LABEL[s.role]}</span>
          ) : null}
          {s.invitePending ? (
            <span className="chip chip-caution">Invited</span>
          ) : s.active === false ? (
            <span className="chip chip-neutral">Archived</span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 text-[12.5px] text-muted">
          <span className="truncate">{s.email}</span>
          {s.phone ? <PhoneLink value={s.phone} /> : null}
        </div>
      </div>

      {/* Rates, right aligned and tabular so the columns read down the
          list rather than jittering with name length. */}
      <div className="tabular hidden text-right sm:block" style={{ width: 190 }}>
        {isSupport ? (
          <div className="text-[12px] text-faint">Not paid through this business</div>
        ) : (
          <>
            <div className="text-[13px] font-bold text-ink">{fmtMoney(s.rateOrdinary)}</div>
            <div className="text-[11.5px] text-faint">
              Sat {fmtMoney(s.rateSaturday)} · Sun {fmtMoney(s.rateSunday)}
            </div>
          </>
        )}
      </div>

      {/* Fixed-width control track: every row's buttons land on the
          same x, by construction rather than by measurement. */}
      <div className="flex shrink-0 items-center gap-1" style={{ width: 116, justifyContent: 'flex-end' }}>
        <a
          className="btn btn-icon btn-sm"
          href={`mailto:${s.email}`}
          title={`Email ${s.name}|${s.email}`}
          aria-label={`Email ${s.name}`}
        >
          <Mail size={15} aria-hidden="true" />
        </a>
        {s.phone ? (
          <a
            className="btn btn-icon btn-sm"
            href={`tel:${s.phone.replace(/[^\d+]/g, '')}`}
            title={`Call ${s.name}|${s.phone}`}
            aria-label={`Call ${s.name}`}
          >
            <Phone size={15} aria-hidden="true" />
          </a>
        ) : (
          <span className="btn btn-icon btn-sm invisible" aria-hidden="true" />
        )}
        <button
          type="button"
          className="btn btn-icon btn-sm"
          onClick={onEdit}
          title={`Edit ${s.name}|Rates, role, contact details and tax settings.`}
          aria-label={`Edit ${s.name}`}
        >
          <Pencil size={15} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function InviteModal({ open, onClose, slug }: { open: boolean; onClose: () => void; slug: string }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('staff');
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [duplicate, setDuplicate] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: (force: boolean) => api.inviteStaff(slug, { name: name.trim(), email: email.trim(), role, force }),
    onSuccess: (res) => {
      setInviteUrl(res.inviteUrl);
      setDuplicate(null);
      void qc.invalidateQueries({ queryKey: qk.staff(slug) });
    },
    onError: (err) => {
      const e = err instanceof ApiError ? err : null;
      if (e?.code === 'possible_duplicate') {
        setDuplicate(e.message);
        return;
      }
      setFailure(e?.message ?? 'Could not send that invite. Try again in a moment.');
    },
  });

  function reset() {
    setName('');
    setEmail('');
    setRole('staff');
    setErrors({});
    setInviteUrl(null);
    setCopied(false);
    setDuplicate(null);
    onClose();
  }

  function submit() {
    const next: typeof errors = {};
    if (!name.trim()) next.name = 'Give the person a name so the roster reads properly.';
    if (!email.trim() || !email.includes('@')) next.email = 'A working email address is needed for the invite.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    invite.mutate(false);
  }

  return (
    <>
      <Modal
        open={open}
        onClose={reset}
        title={inviteUrl ? 'Invite ready' : 'Invite someone to the team'}
        subtitle={
          inviteUrl
            ? 'Send them this link. They set their own password on our site.'
            : 'They get a link, not a password. Nothing sensitive travels by email.'
        }
        footer={
          inviteUrl ? (
            <button type="button" className="btn btn-primary" onClick={reset}>
              Done
            </button>
          ) : (
            <>
              <button type="button" className="btn" onClick={reset}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={submit} disabled={invite.isPending}>
                {invite.isPending ? <Spinner size={15} label="Creating" /> : <UserPlus size={15} aria-hidden="true" />}
                Create invite
              </button>
            </>
          )
        }
      >
        {inviteUrl ? (
          <div>
            <div
              className="flex items-center gap-2 rounded-card border p-3"
              style={{ background: 'var(--sp-surface-2)', borderColor: 'var(--sp-border)' }}
            >
              <code className="min-w-0 flex-1 break-all text-[12.5px]" style={{ fontFamily: 'var(--sp-font-mono)' }}>
                {inviteUrl}
              </code>
              <button
                type="button"
                className="btn btn-icon btn-sm shrink-0"
                title="Copy the invite link|Paste it into a message to them."
                aria-label="Copy the invite link"
                onClick={() => {
                  void navigator.clipboard?.writeText(inviteUrl).then(() => setCopied(true));
                }}
              >
                {copied ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
              </button>
            </div>
            <p className="mt-3 text-[13px] leading-relaxed text-muted">
              The link works once and expires. If it lapses, invite them again and a fresh link is issued.
            </p>
          </div>
        ) : (
          <>
            {duplicate ? (
              <div
                className="mb-4 rounded-card border p-3.5 text-[13px] leading-relaxed"
                style={{ background: 'var(--sp-caution-soft)', borderColor: 'var(--sp-caution)' }}
              >
                <strong className="block text-ink">This might be somebody you already have.</strong>
                <span className="block text-muted">{duplicate}</span>
                <button type="button" className="btn btn-sm mt-2.5" onClick={() => invite.mutate(true)}>
                  Add them anyway, they are a different person
                </button>
              </div>
            ) : null}
            <Field label="Name" error={errors.name} id="invite-name">
              <input
                id="invite-name"
                className="field"
                value={name}
                aria-invalid={errors.name ? 'true' : undefined}
                onChange={(e) => setName(e.target.value)}
                placeholder="Their full name"
              />
            </Field>
            <Field label="Email" error={errors.email} id="invite-email">
              <input
                id="invite-email"
                className="field"
                type="email"
                value={email}
                aria-invalid={errors.email ? 'true' : undefined}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Their email address"
              />
            </Field>
            <Field label="Role" id="invite-role" hint="Managers can build and publish rosters. Owners can also finalise pay.">
              <select id="invite-role" className="field" value={role} onChange={(e) => setRole(e.target.value as Role)}>
                <option value="staff">Team member</option>
                <option value="manager">Manager</option>
                <option value="owner">Owner</option>
              </select>
            </Field>
          </>
        )}
      </Modal>
      <OutcomeModal
        open={failure !== null}
        onClose={() => setFailure(null)}
        ok={false}
        title="Could not create the invite"
        message={failure ?? ''}
      />
    </>
  );
}

function StaffDrawer({
  member,
  slug,
  isOwner,
  isDev,
  onClose,
}: {
  member: StaffMember | null;
  slug: string;
  isOwner: boolean;
  isDev: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<StaffMember | null>(member);
  const [failure, setFailure] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Re-seed when a different person is opened.
  if (member && form?.userId !== member.userId) setForm(member);

  const save = useMutation({
    mutationFn: (v: Partial<StaffMember>) => api.updateStaff(slug, member?.userId ?? '', v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.staff(slug) });
      setSaved(true);
    },
    onError: (err) =>
      setFailure(err instanceof ApiError ? err.message : 'Could not save those details. Try again in a moment.'),
  });

  const set = <K extends keyof StaffMember>(k: K, v: StaffMember[K]) =>
    setForm((prev) => (prev ? { ...prev, [k]: v } : prev));

  return (
    <>
      <Drawer
        open={member !== null}
        onClose={onClose}
        title={form?.name ?? 'Team member'}
        subtitle={
          member
            ? `${ROLE_LABEL[member.role]} · ${
                member.invitePending
                  ? 'invited, has not accepted yet'
                  : member.hasAccount === false
                    ? 'no password set yet'
                    : member.active === false
                      ? 'archived'
                      : 'active'
              }`
            : undefined
        }
        footer={
          <>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={save.isPending || !form}
              onClick={() =>
                form &&
                save.mutate({
                  name: form.name,
                  email: form.email,
                  phone: form.phone,
                  role: form.role,
                  active: form.active,
                  // A number input hands back a STRING, and the API takes
                  // money as a number. Blank reads as 0 rather than NaN so an
                  // emptied box cannot post rubbish into somebody's pay.
                  rateOrdinary: money(form.rateOrdinary),
                  rateSaturday: money(form.rateSaturday),
                  rateSunday: money(form.rateSunday),
                  claimsTaxFreeThreshold: form.claimsTaxFreeThreshold,
                  tfnProvided: form.tfnProvided,
                  superFundName: form.superFundName ?? null,
                  superMemberNumber: form.superMemberNumber ?? null,
                })
              }
            >
              {save.isPending ? <Spinner size={15} label="Saving" /> : null}
              Save changes
            </button>
          </>
        }
      >
        {form ? (
          <>
            <h3 className="mb-3 text-[12px] font-bold uppercase tracking-wide text-faint">Who they are</h3>
            <Field label="Name" id="staff-name" hint="How they appear on the roster.">
              <input
                id="staff-name"
                className="field"
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
              />
            </Field>
            <Field
              label="Email"
              id="staff-email"
              hint="This is their login and where their invite goes. Changing it also updates an invite they have not accepted yet."
            >
              <input
                id="staff-email"
                className="field"
                type="email"
                autoComplete="off"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
              />
            </Field>

            <h3 className="mb-3 mt-5 text-[12px] font-bold uppercase tracking-wide text-faint">Pay rates</h3>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Weekday" id="rate-ordinary">
                <input
                  id="rate-ordinary"
                  className="field tabular"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.rateOrdinary}
                  onChange={(e) => set('rateOrdinary', e.target.value)}
                />
              </Field>
              <Field label="Saturday" id="rate-sat">
                <input
                  id="rate-sat"
                  className="field tabular"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.rateSaturday}
                  onChange={(e) => set('rateSaturday', e.target.value)}
                />
              </Field>
              <Field label="Sunday" id="rate-sun">
                <input
                  id="rate-sun"
                  className="field tabular"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.rateSunday}
                  onChange={(e) => set('rateSunday', e.target.value)}
                />
              </Field>
            </div>
            <p className="-mt-1 mb-5 text-[12px] leading-relaxed text-faint">
              Dollars per hour. The roster prices every shift automatically from the day it falls on, so a Sunday
              shift is costed at the Sunday rate without anybody choosing it.
            </p>

            <h3 className="mb-3 text-[12px] font-bold uppercase tracking-wide text-faint">Details</h3>
            <Field label="Phone" id="staff-phone" hint="Shown on the team list as a tap-to-call link.">
              <input
                id="staff-phone"
                className="field"
                type="tel"
                value={form.phone ?? ''}
                onChange={(e) => set('phone', e.target.value)}
                placeholder="Add a mobile number"
              />
            </Field>
            <Field
              label="Role"
              id="staff-role"
              hint={isOwner ? undefined : 'Only the owner can change somebody to or from owner.'}
            >
              <select
                id="staff-role"
                className="field"
                value={form.role}
                onChange={(e) => set('role', e.target.value as Role)}
              >
                <option value="staff">Team member</option>
                <option value="manager">Manager</option>
                <option value="owner" disabled={!isOwner}>
                  Owner
                </option>
                {/* Support access is ours to hand out, so the option only
                    exists for somebody who already holds it. */}
                {isDev || form.role === 'dev' ? (
                  <option value="dev" disabled={!isDev}>
                    Sneaky Panda support
                  </option>
                ) : null}
              </select>
            </Field>

            <h3 className="mb-3 mt-5 text-[12px] font-bold uppercase tracking-wide text-faint">Tax and super</h3>
            <label className="mb-3 flex items-start gap-2.5 text-[13.5px] leading-snug">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={form.claimsTaxFreeThreshold}
                onChange={(e) => set('claimsTaxFreeThreshold', e.target.checked)}
              />
              <span>
                <span className="font-semibold text-ink">Claims the tax free threshold</span>
                <span className="block text-muted">
                  Standard for somebody whose main job this is. Withholding uses scale 2 when ticked.
                </span>
              </span>
            </label>
            <label className="mb-4 flex items-start gap-2.5 text-[13.5px] leading-snug">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={form.tfnProvided ?? true}
                onChange={(e) => set('tfnProvided', e.target.checked)}
              />
              <span>
                <span className="font-semibold text-ink">Tax file number provided</span>
                <span className="block text-muted">Without one, withholding jumps to the no TFN rate.</span>
              </span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Super fund" id="staff-fund">
                <input
                  id="staff-fund"
                  className="field"
                  value={form.superFundName ?? ''}
                  onChange={(e) => set('superFundName', e.target.value)}
                  placeholder="Add their super fund"
                />
              </Field>
              <Field label="Member number" id="staff-member">
                <input
                  id="staff-member"
                  className="field"
                  value={form.superMemberNumber ?? ''}
                  onChange={(e) => set('superMemberNumber', e.target.value)}
                />
              </Field>
            </div>

            <h3 className="mb-3 mt-2 text-[12px] font-bold uppercase tracking-wide text-faint">Access</h3>
            <label className="flex items-start gap-2.5 text-[13.5px] leading-snug">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={form.active}
                onChange={(e) => set('active', e.target.checked)}
              />
              <span>
                <span className="font-semibold text-ink">Active</span>
                <span className="block text-muted">
                  Turn this off when somebody leaves. They disappear from the roster and can no longer sign in, and
                  their past shifts and payslips stay exactly as they were.
                </span>
              </span>
            </label>
          </>
        ) : null}
      </Drawer>
      <OutcomeModal
        open={failure !== null}
        onClose={() => setFailure(null)}
        ok={false}
        title="Could not save"
        message={failure ?? ''}
      />
      <OutcomeModal
        open={saved}
        onClose={() => {
          setSaved(false);
          onClose();
        }}
        ok
        title="Saved"
        message={`${form?.name ?? 'Their'} details are up to date. New rates apply to every shift costed from now on.`}
      />
    </>
  );
}

export default function TeamPage() {
  const { tenant, isOwner, isDev } = useSession();
  const slug = tenant?.tenantSlug ?? '';
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<StaffMember | null>(null);
  const [q, setQ] = useState('');

  const query = useQuery({ queryKey: qk.staff(slug), queryFn: () => api.staff(slug), enabled: slug !== '' });

  if (query.isLoading) return <LoadingBlock label="Loading the team" rows={6} />;
  if (query.isError) return <ErrorState error={query.error} what="the team" onRetry={() => void query.refetch()} />;

  const staff = query.data?.items ?? [];
  // Fourteen people is already past the point of scanning. The filter is on
  // name and email, which is what a boss actually knows about somebody.
  const needle = q.trim().toLowerCase();
  const matches = (s: StaffMember) =>
    needle === '' || s.name.toLowerCase().includes(needle) || s.email.toLowerCase().includes(needle);
  // Support accounts are not part of the business, so they are counted and
  // listed separately from the people who actually work here.
  const team = staff.filter((s) => s.role !== 'dev' && matches(s));
  const platform = staff.filter((s) => s.role === 'dev');
  const active = team.filter((s) => s.active !== false).sort(byRoleThenName);
  // Waiting on them is not the same as removed, so they are counted apart.
  const invited = team.filter((s) => s.active === false && s.invitePending).sort(byRoleThenName);
  const archived = team.filter((s) => s.active === false && !s.invitePending).sort(byRoleThenName);
  const inactive = [...invited, ...archived];

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <header className="mb-5 flex flex-wrap items-end gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-[20px] font-extrabold tracking-tight text-ink">Team</h1>
          <p className="text-[13px] text-muted">
            {active.length} active
            {invited.length > 0 ? `, ${invited.length} invited` : ''}
            {archived.length > 0 ? `, ${archived.length} archived` : ''}
          </p>
        </div>
        <div className="relative w-full sm:w-60">
          <Search
            size={15}
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: 'var(--sp-text-faint)' }}
          />
          <input
            className="field pl-9"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find someone"
            aria-label="Find a team member by name or email"
          />
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setInviting(true)}>
          <UserPlus size={15} aria-hidden="true" />
          Invite someone
        </button>
      </header>

      {staff.length > 0 && team.length === 0 && needle !== '' ? (
        <EmptyState
          icon={<Search size={20} aria-hidden="true" />}
          title={`Nobody matches "${q.trim()}"`}
          body="Try part of a name, or their email address."
        />
      ) : staff.length === 0 ? (
        <EmptyState
          title="Nobody on the team yet"
          body="Invite your first team member and they will appear on the roster straight away."
          action={
            <button type="button" className="btn btn-primary" onClick={() => setInviting(true)}>
              <UserPlus size={15} aria-hidden="true" />
              Invite someone
            </button>
          }
        />
      ) : (
        <div className="card overflow-hidden">
          {[...active, ...inactive].map((s, i) => (
            <MemberRow key={s.userId} s={s} first={i === 0} onEdit={() => setEditing(s)} />
          ))}
        </div>
      )}

      {platform.length > 0 ? (
        <section className="mt-7">
          <h2 className="text-[12px] font-bold uppercase tracking-wide text-faint">Platform access</h2>
          <p className="mt-1 mb-2 text-[12.5px] leading-snug text-muted">
            Sneaky Panda support accounts. They can help with setup and fix problems, but they are not part of
            your team: never rostered, never paid, and never counted in your wage totals.
          </p>
          <div className="card overflow-hidden">
            {platform.map((s, i) => (
              <MemberRow key={s.userId} s={s} first={i === 0} onEdit={() => setEditing(s)} />
            ))}
          </div>
        </section>
      ) : null}

      <InviteModal open={inviting} onClose={() => setInviting(false)} slug={slug} />
      <StaffDrawer member={editing} slug={slug} isOwner={isOwner} isDev={isDev} onClose={() => setEditing(null)} />
    </div>
  );
}
